import {
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";

import { cn } from "@btnet/utilities-core";

import "./bottomSheet.less";
import { type IBTDrawerProps } from "./BTDrawer";
import { DraggableSheetHandle } from "./DraggableSheetHandle";

/**
 * Marks a drawer as a bottom sheet. Load-bearing beyond styling: `BTPopover`, `BTLink`, and the
 * app's own `getClosestModal` all test for this class to decide whether a popup may portal out
 * of the overlay rather than being clipped by a short sheet.
 */
const BOTTOM_SHEET_CLASS = "bt-bottom-sheet";

/**
 * The grabber pill's class, handed to whichever handle this renders. Module-private: both
 * handle components take it as a prop, so the class is declared once here alongside the
 * sheet's other class names rather than being owned by one of them.
 */
const BOTTOM_SHEET_HANDLE_CLASS = "bt-bottom-sheet-handle";

/**
 * The sheet's content area, and part of the flex chain that gives the sheet its auto height —
 * `bottomSheetDrawerStyle` → `bottomSheetBodyStyle` → this — so content scrolls inside the
 * sheet rather than growing it past `BOTTOM_SHEET_MAX_HEIGHT`.
 *
 * A class rather than a wrapper component because `BTPeek` puts it on a root it already
 * renders: a wrapper present in only one presentation changes the tree's shape, and React
 * remounts the content at the breakpoint — the thing its single drawer exists to avoid.
 */
const BOTTOM_SHEET_BODY_CLASS = "bt-bottom-sheet-body";

/**
 * How tall a sheet may grow, leaving the rest of the viewport as scrim so what it covers stays
 * visible behind it. The wrapper, the drawer and `fillHeight` all measure against this, so it
 * has to be one value rather than three.
 */
const BOTTOM_SHEET_MAX_HEIGHT = "85vh";

/**
 * Applied to `.ant-drawer-content-wrapper`, over rc-drawer's own inline transform.
 *
 * Emitting no transform here is deliberate: a `translateY(0)` would override the
 * `translateY(100%)` rc-drawer applies to animate the sheet closed. A drag offset is spread on
 * top of this while a swipe is in progress, and drops back to exactly this object at rest for
 * the same reason.
 */
export const bottomSheetContentWrapperStyle: CSSProperties = {
  height: "auto",
  maxHeight: BOTTOM_SHEET_MAX_HEIGHT,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  overflow: "hidden",
};

/**
 * `fillHeight`'s wrapper style: the same sheet with a fixed height rather than a cap, so its
 * box is independent of what is inside it.
 */
const bottomSheetFilledContentWrapperStyle: CSSProperties = {
  ...bottomSheetContentWrapperStyle,
  height: BOTTOM_SHEET_MAX_HEIGHT,
};

const bottomSheetDrawerStyle: CSSProperties = {
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  display: "flex",
  flexDirection: "column",
  maxHeight: BOTTOM_SHEET_MAX_HEIGHT,
};

const bottomSheetBodyStyle: CSSProperties = {
  padding: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  flex: 1,
  minHeight: 0,
};

export interface IUseBottomSheetOptions {
  /**
   * Whether to present as a bottom sheet at all. When false every returned value is inert —
   * undefined props and a null handle — so a caller can switch presentation on a viewport
   * check without calling the hook conditionally.
   * @default true
   */
  enabled?: boolean | undefined;
  visible: boolean | undefined;
  onClose: ((e?: MouseEvent | KeyboardEvent) => void) | undefined;
  /**
   * Open at the sheet's full height rather than sizing to the content.
   *
   * Leave this off for content with a height of its own — a short menu or form, where a taller
   * sheet would just be empty space. Turn it on where the content expects to be *given* one: a
   * scrolling list, a viewer, anything that renders before its data arrives. Such content
   * measures as nearly empty while the sheet is opening, so an auto-height sheet opens as a
   * sliver and jumps once the content settles.
   * @default false
   */
  fillHeight?: boolean | undefined;
  /** The caller's own drawer class. Merged with the sheet's, never replacing it. */
  className?: string | undefined;
}

export interface IUseBottomSheetReturn {
  /**
   * Spread onto the `BTDrawer`. Empty when disabled, so a caller can spread unconditionally and
   * supply its own value with `??` for the other presentation. Disabled omits the keys rather
   * than setting them to `undefined`, so a caller can forward them to a target whose optional
   * props reject an explicit `undefined` under `exactOptionalPropertyTypes`.
   */
  drawerProps: Partial<
    Pick<
      IBTDrawerProps,
      | "placement"
      | "closable"
      | "className"
      | "contentWrapperStyle"
      | "drawerStyle"
      | "bodyStyle"
    >
  >;
  /** Render as the drawer's first child. Null when disabled. */
  handle: ReactNode;
  /**
   * Class for the sheet's content area. Returned rather than applied by a wrapper component
   * so a caller can put it on an element it already renders — an extra div that exists in
   * only one presentation changes the tree's shape and remounts the content when the caller
   * switches between them.
   */
  bodyClassName: string | undefined;
  /** Whether sheet presentation is active, for callers that also branch their own markup. */
  isSheet: boolean;
}

/**
 * Bottom sheet presentation for a `BTDrawer`: the sheet enters from the bottom with rounded
 * top corners, sizes itself to its content up to its maximum height, and carries a grabber
 * handle. Pass `fillHeight` for content that needs the full height rather than sizing to it.
 *
 * A hook rather than a component because this is a *mode a drawer can be in*, not a kind of
 * component. `BTBottomSheet` is the ready-made wrapper for the common case; a caller that
 * already renders its own `BTDrawer` — and switches it between presentations, as `BTPeek`
 * does — sprinkles the behavior on instead, keeping one drawer across the switch rather than
 * swapping component types and remounting its content.
 */
export function useBottomSheet({
  enabled = true,
  visible,
  onClose,
  fillHeight = false,
  className,
}: IUseBottomSheetOptions): IUseBottomSheetReturn {
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  /**
   * Dismissal from the drag handle — a swipe, a tap, or its activation keys. `event` is the DOM
   * event that triggered it, forwarded to `onClose` the way the scrim and Escape paths do; a
   * drag has no single event to attribute the close to, so it passes none.
   */
  const closeSheet = (event?: KeyboardEvent | MouseEvent) => {
    // Zero the offset before closing so this render drops our transform override and rc-drawer
    // regains control of the wrapper — otherwise translateY(offset) would beat its
    // translateY(100%) and the sheet would vanish instead of sliding out. Both updates land in
    // the same call stack, so the intermediate state is never painted.
    setDragOffsetPx(0);
    setIsDragging(false);
    onClose?.(event);
  };

  // A close from the scrim, Escape, or native back can land mid-drag, so reset on every
  // visibility change and the next open starts at rest. The handle keeps its own `didDrag` and
  // clears it on mount.
  useEffect(() => {
    setDragOffsetPx(0);
    setIsDragging(false);
  }, [visible]);

  const contentWrapperStyle = useMemo((): CSSProperties => {
    const base = fillHeight
      ? bottomSheetFilledContentWrapperStyle
      : bottomSheetContentWrapperStyle;

    if (dragOffsetPx <= 0 && !isDragging) {
      // At rest this must be the shared object untouched — it deliberately emits no transform,
      // so rc-drawer's own translateY(100%) is what animates the sheet closed.
      return base;
    }

    return {
      ...base,
      transform: `translateY(${dragOffsetPx}px)`,
      // antd puts a 0.3s transform transition on every drawer child; leaving it on makes the
      // sheet lag behind the finger. Restoring it on release is what animates the snap back.
      transition: isDragging ? "none" : undefined,
      willChange: isDragging ? "transform" : undefined,
    };
  }, [dragOffsetPx, isDragging, fillHeight]);

  if (!enabled) {
    return {
      // The caller's own class still applies — the drawer is rendered either way, it just
      // isn't a sheet.
      drawerProps: className === undefined ? {} : { className },
      handle: null,
      bodyClassName: undefined,
      isSheet: false,
    };
  }

  return {
    drawerProps: {
      placement: "bottom",
      closable: false,
      // BOTTOM_SHEET_CLASS is load-bearing beyond styling: BTPopover, BTLink and the app's own
      // container resolvers test for it to decide whether a popup may portal out of a short
      // sheet. A caller's class is merged with it, never substituted for it.
      className: cn(BOTTOM_SHEET_CLASS, className),
      contentWrapperStyle,
      drawerStyle: bottomSheetDrawerStyle,
      bodyStyle: bottomSheetBodyStyle,
    },
    handle: (
      <DraggableSheetHandle
        className={BOTTOM_SHEET_HANDLE_CLASS}
        onOffsetChange={setDragOffsetPx}
        onDraggingChange={setIsDragging}
        onDismiss={closeSheet}
      />
    ),
    bodyClassName: BOTTOM_SHEET_BODY_CLASS,
    isSheet: true,
  };
}
